import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type {
  PublicQuestionListItem,
  PaginationMeta,
  PublicVisibleStatus,
} from '@find-my-part/types';
import { widgetApi } from '../api/client';
import {
  loadPublicQuestionList,
  peekPublicQuestionList,
  publicListCacheKey,
  revalidatePublicQuestionList,
} from '../api/publicQuestionCache';

export interface UsePublicQuestionsOptions {
  page?: number;
  limit?: number;
  enabled?: boolean;
  status?: readonly PublicVisibleStatus[];
}

export interface UsePublicQuestionsResult {
  questions: PublicQuestionListItem[];
  pagination: PaginationMeta | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface PublicQuestionsState {
  /** Includes cache identity plus enabled/initialisation posture. */
  key: string;
  questions: PublicQuestionListItem[];
  pagination: PaginationMeta | null;
  isLoading: boolean;
  error: string | null;
}

export function usePublicQuestions(
  options: UsePublicQuestionsOptions = {}
): UsePublicQuestionsResult {
  const { page = 1, limit = 10, enabled = true, status } = options;
  // The transport treats status as a set. Canonicalising here keeps hook
  // identity aligned with the request/cache key rather than restarting an
  // effect only because a caller supplied the same statuses in another order.
  const statusKey = status?.length ? [...new Set(status)].sort().join(',') : '';
  const initialized = widgetApi.isInitialized();
  const readOptions = useMemo(
    () => ({
      page,
      limit,
      status: statusKey ? (statusKey.split(',') as PublicVisibleStatus[]) : undefined,
    }),
    [page, limit, statusKey]
  );
  const cacheKey = publicListCacheKey(readOptions);
  const renderKey = `${enabled ? 'enabled' : 'disabled'}|${initialized ? 'ready' : 'uninitialised'}|${cacheKey}`;

  // Build a render-time snapshot for EVERY resource-key transition, not only
  // for the first mount. Effects run after commit; resetting state there would
  // let page N's rows commit once under page N+1. A key-scoped snapshot makes a
  // warm destination available in its first commit and a cold destination
  // empty/loading in its first commit, while the effect synchronises owned
  // state immediately afterwards.
  const keySnapshot = useMemo<PublicQuestionsState>(() => {
    if (!enabled) {
      return { key: renderKey, questions: [], pagination: null, isLoading: false, error: null };
    }
    if (!initialized) {
      return {
        key: renderKey,
        questions: [],
        pagination: null,
        isLoading: false,
        error: 'WIDGET_INIT_ERROR',
      };
    }

    const cached = peekPublicQuestionList(readOptions);
    return {
      key: renderKey,
      questions: cached.data?.questions ?? [],
      pagination: cached.data?.pagination ?? null,
      isLoading: !cached.data,
      error: null,
    };
  }, [renderKey, enabled, initialized, readOptions]);

  const [state, setState] = useState<PublicQuestionsState>(keySnapshot);
  const visibleState = state.key === renderKey ? state : keySnapshot;
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * ADR-028 Slice 3. `force` marks an explicit visitor retry, which always
   * performs a real network attempt (still de-duplicated against an identical
   * in-flight request) instead of being satisfied from memory.
   */
  const fetchQuestions = useCallback(
    async (force = false) => {
      if (!enabled || !initialized) {
        requestIdRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setState({
          key: renderKey,
          questions: [],
          pagination: null,
          error: enabled ? 'WIDGET_INIT_ERROR' : null,
          isLoading: false,
        });
        return;
      }

      // A warm entry is adopted BEFORE any state transition, so a cached
      // browse -> detail -> back navigation never flashes the blocking
      // skeleton. `stale` still paints immediately; only the revalidation is
      // deferred to the background.
      if (!force) {
        const cached = peekPublicQuestionList(readOptions);
        if (cached.data) {
          const currentRequestId = ++requestIdRef.current;
          abortControllerRef.current?.abort();
          abortControllerRef.current = null;
          setState({
            key: renderKey,
            questions: cached.data.questions,
            pagination: cached.data.pagination,
            error: null,
            isLoading: false,
          });

          if (cached.freshness === 'stale') {
            // Adopt whatever the ONE shared background refresh returns. It is
            // deliberately not paired with a `load()` follow-up: `load()` does
            // not consult the failed-revalidation cooldown, so pairing them
            // would re-request a failing key on every stale render. A `null`
            // here means the cache is intentionally declining (cooldown), and
            // the stale data already on screen stands.
            const pending = revalidatePublicQuestionList(readOptions);
            if (pending) {
              void pending.then((result) => {
                // Generation guard: a background refresh must never paint over
                // a newer page the visitor has already moved to.
                if (requestIdRef.current !== currentRequestId) return;
                if (!result.admitted) return;
                setState((current) =>
                  current.key === renderKey
                    ? {
                        ...current,
                        questions: result.data.questions,
                        pagination: result.data.pagination,
                      }
                    : current
                );
              }, () => undefined);
            }
          }
          return;
        }
      }

      const currentRequestId = ++requestIdRef.current;
      // The cache owns the transport-level AbortController for the shared
      // flight. This controller stays purely local: it records that this hook
      // instance has moved on, and is never handed to the shared request, so
      // one consumer unmounting cannot cancel work another consumer still
      // needs.
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // A miss includes an expired entry. Never retain the old payload behind
      // `isLoading`: the maximum-age boundary is a blocking reload, and hook
      // consumers must not be able to observe expired rows as current data.
      setState({
        key: renderKey,
        questions: [],
        pagination: null,
        error: null,
        isLoading: true,
      });

      try {
        const response = await loadPublicQuestionList(readOptions, { force });
        if (controller.signal.aborted || requestIdRef.current !== currentRequestId) return;

        if (response.success) {
          setState({
            key: renderKey,
            questions: response.data.questions,
            pagination: response.data.pagination,
            error: null,
            isLoading: false,
          });
        } else {
          setState({
            key: renderKey,
            questions: [],
            pagination: null,
            error: response.error.message,
            isLoading: false,
          });
        }
      } catch (err) {
        if (controller.signal.aborted || requestIdRef.current !== currentRequestId) return;
        setState({
          key: renderKey,
          questions: [],
          pagination: null,
          error: err instanceof Error ? err.message : 'Failed to load questions',
          isLoading: false,
        });
      } finally {
        if (!controller.signal.aborted && requestIdRef.current === currentRequestId) {
          setState((current) =>
            current.key === renderKey ? { ...current, isLoading: false } : current
          );
        }
      }
    },
    [enabled, initialized, readOptions, renderKey]
  );

  useEffect(() => {
    void fetchQuestions();
    const handlePageShow = (event: PageTransitionEvent): void => {
      // BFCache restores retain this hook, its state, and the module cache; no
      // mount effect reruns. Re-enter the ordinary cache path so exact fresh,
      // stale/SWR, and expired/blocking boundaries still hold after suspension.
      if (event.persisted) void fetchQuestions();
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      abortControllerRef.current?.abort();
    };
  }, [fetchQuestions]);

  const refetch = useCallback(() => fetchQuestions(true), [fetchQuestions]);

  return {
    questions: visibleState.questions,
    pagination: visibleState.pagination,
    isLoading: visibleState.isLoading,
    error: visibleState.error,
    refetch,
  };
}
